import {useNavigate} from "react-router-dom";
import {Pencil} from "lucide-react";
import {Trash2} from 'lucide-react';
import {LogOut} from 'lucide-react';
import {Tooltip} from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import TrafficLight from "./components/TrafficLight";
import {toast} from "react-toastify";
import {Toaster} from "react-hot-toast";
import { Download } from 'lucide-react';
import { useEffect, useState, useRef, useCallback } from 'react';

const EventCard = ({event, onDelete, onLeave, userId, searchTerm, filters}) => {
    const navigate = useNavigate();
    const [lastTap, setLastTap] = useState(0);
    const cardRef = useRef(null);

    const navigateToEventDetail = useCallback(() => {
        let eventUrl = '/events/view-event/' + event.id;
        if (event.revenueevent) {
            eventUrl = '/events/view-revenue-event/' + event.id;
        }
        navigate(eventUrl, {state: {event: event, searchTerm: searchTerm, filters: filters}});
    }, [navigate, event, searchTerm, filters]);

    // Add touch event listeners for mobile
    useEffect(() => {
        if (!cardRef.current) return;

        const card = cardRef.current;

        // Mobile touch handler
        const handleTouchStart = (e) => {
            const now = Date.now();
            const DOUBLE_TAP_DELAY = 300; // milliseconds

            if (now - lastTap < DOUBLE_TAP_DELAY) {
                // Double tap detected
                e.preventDefault(); // Prevent zooming or other default behaviors
                navigateToEventDetail();
            }

            setLastTap(now);
        };

        // Add touch event listeners
        card.addEventListener('touchstart', handleTouchStart);

        // Cleanup
        return () => {
            card.removeEventListener('touchstart', handleTouchStart);
        };
    }, [lastTap, navigateToEventDetail]); // Include proper dependencies

    // Desktop double-click handler - this works fine
    const handleDoubleClick = (e) => {
        // Desktop double-click
        navigateToEventDetail();
    };

    // Function to get a random image from the available set
    const getRandomImage = () => {
        // List of  6 available  images, note there really are 24 images
        const imageList = ['1.jpg', '2.jpg', '3.jpg', '4.jpg', '5.jpg', '6.jpg'];

        if (event.id) {
            // Use the event ID to create a deterministic selection from 24 possible images
            const imageIndex = event.id % 24;
            return `/events/images/${imageIndex}.jpg`;
        } else {
            // Fallback to truly random selection if no event ID
            const randomIndex = Math.floor(Math.random() * imageList.length);
            return `/images/${imageList[randomIndex]}`;
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "-";

        const [year, month, day] = dateStr.split('-');
        return new Date(year, month - 1, day).toLocaleDateString('en-US', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    const openForum = (url) =>{
        window.open(url, '_blank').focus();
    }

    // Function to determine background color based on event type
    const getEventTypeBackground = (eventType) => {
        if (!eventType) return "bg-gray-200"; // Default background

        if (eventType.includes("Trail Run") && eventType.includes("single day")) {
            return "bg-[#e7f0e9]"; // Light Pine Green
        } else if (eventType.includes("Trail Run") && eventType.includes("multi-day")) {
            return "bg-[#e0eaf0]"; // Deep Sky Blue
        } else if (eventType.includes("BaseCamp") || eventType.includes("campout")) {
            return "bg-[#f5f0e6]"; // Warm Sand
        } else if (eventType.includes("Meetup")) {
            return "bg-[#faf0e6]"; // Soft Orange
        } else if (eventType.includes("Volunteer")) {
            return "bg-[#f0eef5]"; // Pale Lavender
        }

        return "bg-gray-200"; // Default fallback
    };

    const handleEditClick = () => {
        navigate(`/events/edit-event/${event.id}`, {state: {event : event, searchTerm : searchTerm, filters : filters}});
    };

    const handleJoin = (event) => {
        if ( userId === "" || userId.length === 0) {
            toast.error("You need to be logged in! Please go to www.overlandtrailguides.com");
            return;
        }

        const eventType = event.eventtype;


        // If admin has sus-ended subscriptions do NOT check for subscriptions
        if ( event.suspendsubs) {
            if (eventType === "Meetup" || eventType.includes("Volunteer")) {
                navigate(`/events/sm-join-event/${event.id}/${userId}`, {state: {event, userId}});
            } else {
                navigate(`/events/join-event/${event.id}/${userId}`, {state: {event, userId}});
            }
            return;
        }

        fetch('/otgservices/checkSubscription.php')
            .then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                return response.json();
            })
            .then(json => {
                if ( !eventType.suspendsubs) {
                    if (!eventType.includes("BaseCamp")) {
                        if (!json.success) {
                            toast.error("You must have a subscription to Join. Please go to www.overlandtrailguides.com and subscribe");
                            return;
                        }
                    }
                }
                if (eventType === "Meetup" || eventType.includes("Volunteer")) {
                    navigate(`/events/sm-join-event/${event.id}/${userId}`, {state: {event, userId}});
                } else {
                    navigate(`/events/join-event/${event.id}/${userId}`, {state: {event, userId}});
                }
            })
            .catch(error => {
                console.error('Error checking subscription:', error);
                toast.error("Error checking subscription status. Please try again.");
            });


    };

    const handleDeleteClick = () => {
        onDelete(event);
    };

    const handleLeaveClick = () => {
        onLeave(event);
    };

    const handleDownloadClick = (event) => {
        let eventId = event.id;
        window.location.href = `/otgservices/downloadEventGoers.php?event_id=${eventId}`;
    };

    return (
        <>
            <Toaster position="top-right" reverseOrder={false} />
            <div
                id={`event-${event.id}`}></div>
            <div
                ref={cardRef}
                className={`${getEventTypeBackground(event.eventtype)} shadow-lg rounded-2xl overflow-hidden w-full md:w-full lg:w-full max-w-4xl mx-auto h-full flex flex-row max-h-[600px] relative ${!event.is_public ? 'border-4 border-red-500' : ''} ${event.revenueevent ? 'border-4 border-green-500' : ''} 
                transition-all duration-300
                hover:shadow-2xl hover:ring-4 hover:ring-orange-400 hover:ring-opacity-80 hover:scale-[1.01]`}
                onDoubleClick={handleDoubleClick}
                style={{ cursor: 'pointer' }}
            >
                {/* Rest of the component remains the same... */}

                {/* $ icon for revenue events - desktop only */}
                {event.revenueevent && (
                    <div className="absolute top-2 right-8 z-10 hidden lg:block" id="revenue-event-dollar-desktop">
                        <span className="text-green-500 font-bold text-lg lg:text-xl">$$$</span>
                        <Tooltip
                            anchorSelect="#revenue-event-dollar-desktop"
                            place="top"
                            content="Paid Event"
                        />
                    </div>
                )}

                {/* Lock icon for private events - desktop only */}
                {!event.is_public && (
                    <div className="absolute top-2 right-2 z-10 hidden lg:block" id="private-event-lock-desktop">
                        <span className="text-red-500 text-lg lg:text-xl">🔐</span>
                        <Tooltip
                            anchorSelect="#private-event-lock-desktop"
                            place="top"
                            content="Private Event"
                        />
                    </div>
                )}

                {/* Desktop layout: Image on left, content on right */}
                <div className="hidden lg:flex lg:w-1/2 flex-shrink-0">
                    {!event.imageurl && (
                        <img
                            src={getRandomImage()}
                            alt="Event thumbnail"
                            className="w-full h-full object-cover"
                        />
                    )
                    }
                    {event.imageurl && (
                        <img
                            src={event.imageurl}
                            alt="Event thumbnail"
                            className="w-full h-full object-cover"
                        />)
                    }
                </div>

                {/* Main content section */}
                <div className="flex flex-col flex-grow">
                    {/* Mobile: Top section with title/summary and small image */}
                    <div className="lg:hidden p-2 flex gap-2 items-center">
                        {/* Title and summary on left */}
                        <div className="flex-grow">
                            <h2 className="text-lg font-semibold mb-1">{event.name}</h2>
                            <div className="relative">
                                <textarea
                                    readOnly
                                    value={event.summary || ""}
                                    className="w-full text-xs text-gray-600 resize-none overflow-y-auto border-0 p-0 focus:ring-0 focus:outline-none bg-transparent"
                                    style={{
                                        maxHeight: '3em',
                                        minHeight: '2em'
                                    }}
                                />
                            </div>
                        </div>

                        {/* $ icon for revenue events - mobile */}
                        {event.revenueevent && (
                            <div className="lg:hidden flex-shrink-0" id="revenue-event-dollar-mobile">
                                <span className="text-green-500 font-bold text-medium">$$$</span>
                                <Tooltip
                                    anchorSelect="#revenue-event-dollar-mobile"
                                    place="top"
                                    content="Paid Event"
                                />
                            </div>
                        )}

                        {/* Lock icon for mobile - positioned to the left of the image */}
                        {!event.is_public && (
                            <div className="lg:hidden flex-shrink-0" id="private-event-lock-mobile">
                                <span className="text-red-500 text-medium">🔐</span>
                                <Tooltip
                                    anchorSelect="#private-event-lock-mobile"
                                    place="top"
                                    content="Private Event"
                                />
                            </div>
                        )}

                        {/* Small image on right */}
                        <div className="w-20 h-18 flex-shrink-0">
                            {!event.imageurl && (
                                <img
                                    src={getRandomImage()}
                                    alt="Event thumbnail"
                                    className="w-full h-full object-cover rounded"
                                />
                            )
                            }
                            {event.imageurl && (
                                <img
                                    src={event.imageurl}
                                    alt="Event thumbnail"
                                    className="w-full h-full object-cover rounded"
                                />)
                            }
                        </div>
                    </div>

                    {/* Desktop: Title and summary (same as before) */}
                    <div className="hidden lg:block p-2">
                        <h2 className="text-xl font-semibold mb-1">{event.name}</h2>
                        {/* Summary section with title - transparent background */}
                        <div className="mb-1">
                            <div className="relative">
                                <textarea
                                    readOnly
                                    value={event.summary || ""}
                                    className="w-full text-sm text-gray-600 resize-none overflow-y-auto border-0 p-0 focus:ring-0 focus:outline-none bg-transparent"
                                    style={{
                                        maxHeight: '5em',
                                        minHeight: '5em'
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Rest of content - same for both mobile and desktop */}
                    <div className="p-2 flex-shrink overflow-auto">
                        {!event.hide_dates && (
                        <div className="flex justify-between text-xs lg:text-sm mb-1">
                            <div>
                                <strong>Start:</strong> <span className="hidden sm:inline">{formatDate(event.start_time)}</span><span className="sm:hidden">{formatDate(event.start_time)}</span>
                            </div>
                            <div>
                                <strong>End:</strong> <span className="hidden sm:inline">{formatDate(event.end_time)}</span><span className="sm:hidden">{formatDate(event.end_time)}</span>
                            </div>
                        </div>
                        )}
                        <div className="flex justify-between text-xs lg:text-sm mb-1 flex-wrap">
                            <div className="flex items-center gap-2">
                                <span><strong>Event Host:</strong> {event.leader}</span>
                                {event.leaderavatar && (
                                    <img
                                        src={event.leaderavatar}
                                        alt={`${event.leader} avatar`}
                                        className="w-5 h-5 lg:w-6 lg:h-6 rounded-full object-cover flex-shrink-0"
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                        }}
                                    />
                                )}
                            </div>
                            <div>
                                <strong>Trips:</strong> {event.tripcount}
                            </div>
                            <div>
                                <strong>Kudos:</strong> {event.kudoscount}
                            </div>
                        </div>
                        { !event.revenueevent && (
                            <div className="text-xs lg:text-sm mb-1">
                                <div className="lg:hidden">
                                    <div><strong>Type:</strong> {event.eventtype}</div>
                                    <div><strong>Region:</strong> {event.region}</div>
                                </div>
                                <div className="hidden lg:block">
                                    <strong>Event Type:</strong> {event.eventtype}<br/>
                                    <strong>Region:</strong> {event.region}<br/>
                                </div>
                            </div>
                        )}
                        <div className="text-xs lg:text-sm mb-1">
                            <div className="flex space-x-2 lg:space-x-4 mb-1 text-xs lg:text-sm">
                                <span><strong>Dogs:</strong> {event.dogs_permitted ? "Yes" : "No"}</span>
                                <span><strong>Children:</strong> {event.children_permitted ? "Yes" : "No"}</span>
                            </div>
                        </div>
                    </div>

                    {/* Spacer that pushes the action bar to the bottom */}
                    <div className="flex-grow"></div>

                    {/* Smaller action bar */}
                    <div className="py-2 px-3 border-t border-gray-200 flex justify-between items-center">
                        {/* Left side - Special Event button */}
                        <div className="flex">
                            {event.revenueevent && event.specialeventurl && (
                                <button
                                    type="button"
                                    onClick={() => window.open(event.specialeventurl, '_blank')}
                                    className="bg-orange-500 text-white text-xs lg:text-sm px-2 py-1 lg:px-4 lg:py-2 rounded hover:bg-orange-600"
                                >
                                    Event Registration
                                </button>
                            )}
                        </div>

                        {/* Right side - existing buttons */}
                        <div className="flex items-center">
                            <div className="flex space-x-1 lg:space-x-2">
                                {( event.forumurl && (event.isgoing || event.revenueevent) ) && (
                                    <button
                                        type="button"
                                        onClick={() => openForum(event.forumurl)}
                                        className="bg-orange-500 text-white text-xs lg:text-sm px-2 py-1 lg:px-4 lg:py-2 rounded hover:bg-orange-600"
                                    >
                                        Forum
                                    </button>
                                )}

                                {event.editable && (
                                    <div className="inline-block" id="edit-icon">
                                        <button
                                            type="button"
                                            onClick={handleEditClick}
                                            className="text-orange-500 hover:text-orange-600 p-1 lg:p-2">
                                            <Pencil className="w-3.5 h-3.5 lg:w-6 lg:h-6"/>
                                        </button>
                                        <Tooltip
                                            anchorSelect="#edit-icon"
                                            place="top"
                                            content="Edit this event"
                                        />
                                    </div>
                                )}
                                {event.editable && (
                                    <div className="inline-block" id="delete-icon">
                                        <button
                                            type="button"
                                            onClick={handleDeleteClick}
                                            className="text-red-500 hover:text-red-600 p-1 lg:p-2">
                                            <Trash2 className="w-3.5 h-3.5 lg:w-6 lg:h-6" title="Delete Event"/>
                                        </button>
                                        <Tooltip
                                            anchorSelect="#delete-icon"
                                            place="top"
                                            content="Cancel this event"
                                        />
                                    </div>
                                )}
                                {(event.editable && !event.revenueevent) && (
                                    <div className="inline-block" id="download-icon">
                                        <button
                                            type="button"
                                            onClick={() => handleDownloadClick(event)}
                                            className="text-blue-500 hover:text-blue-600 p-1 lg:p-2">
                                            <Download className="w-3.5 h-3.5 lg:w-6 lg:h-6" title="Download Participants"/>
                                        </button>
                                        <Tooltip
                                            anchorSelect="#download-icon"
                                            place="top"
                                            content="Download all participants in CSV"
                                        />
                                    </div>
                                )}
                                {!event.blocked && (
                                    <>
                                        {(!event.canJoin && !event.revenueevent) ? (
                                            <div className="inline-block" id="leave-icon">
                                                <button
                                                    type="button"
                                                    onClick={handleLeaveClick}
                                                    className="text-red-500 hover:text-red-600 p-1 lg:p-2">
                                                    <LogOut className="w-3.5 h-3.5 lg:w-6 lg:h-6" title="Leave Event"/>
                                                </button>
                                                <Tooltip
                                                    anchorSelect="#leave-icon"
                                                    place="top"
                                                    content="Remove me from this event"
                                                />
                                            </div>
                                        ) : (
                                            <div className="inline-block" id="going-icon">
                                                <TrafficLight/>
                                                <Tooltip
                                                    anchorSelect="#going-icon"
                                                    place="top"
                                                    content="You are going to this event"
                                                />
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>

                            <div className="flex ml-2 space-x-1 lg:space-x-2">
                                <button
                                    type="button"
                                    onClick={() => {
                                        let eventUrl = '/events/view-event/' + event.id;
                                        if ( event.revenueevent) {
                                            eventUrl = '/events/view-revenue-event/' + event.id;
                                        }
                                        navigate(eventUrl, {state: {event : event, searchTerm : searchTerm, filters : filters}})
                                    }}
                                    className="bg-orange-500 text-white text-xs lg:text-sm px-2 py-1 lg:px-4 lg:py-2 rounded hover:bg-orange-600">
                                    View
                                </button>
                                {(event.canJoin && !event.revenueevent) && (
                                    <button
                                        type="button"
                                        onClick={() => handleJoin(event)}
                                        className="bg-orange-500 text-white text-xs lg:text-sm px-2 py-1 lg:px-4 lg:py-2 rounded hover:bg-orange-600"
                                    >
                                        Join
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default EventCard;